import type { RendererProps } from "../core/element-registry"
import { useRenderContext, RenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import type { CogoElement } from "../core/types"

/**
 * List renderer — renders a data-driven list of items using an item template.
 *
 * JSON shape:
 * {
 *   "type": "list",
 *   "options": {
 *     "data": "#{state.features}",        // array binding
 *     "itemRef": "$ref:definitions/...",   // item template ref (resolved by ref-expander)
 *     "gap": 12                            // spacing between items
 *   }
 * }
 *
 * After ref-expansion, `options.itemRef` becomes a CogoElement tree
 * that gets iterated over each item in the data array.
 */
export function ListRenderer({ element }: RendererProps) {
  const ctx = useRenderContext()
  const { resolveSymbol } = ctx

  const options = element.options ?? {}
  const dataExpr = options.data as string | undefined
  const gap = options.gap as number | undefined
  const direction = options.direction as string | undefined
  const itemTemplate = options.itemRef as CogoElement | string | undefined

  // Resolve data array from state
  const rawData = dataExpr ? resolveSymbol(dataExpr) : undefined
  const items = Array.isArray(rawData) ? rawData : []

  if (items.length === 0) return null

  // If itemRef is still a string (not expanded), we can't render
  if (typeof itemTemplate === "string" || !itemTemplate) {
    // Fallback: render items as simple text if they have a "text" property
    return (
      <div className={direction === "row" ? "flex flex-row" : "flex flex-col"} style={gap ? { gap: `${gap}px` } : undefined}>
        {items.map((item, i) => {
          const text = typeof item === "string" ? item : (item as Record<string, unknown>)?.text
          return text ? <span key={i} className="text-sm">{String(text)}</span> : null
        })}
      </div>
    )
  }

  // Render each item with the template, providing item context
  return (
    <div className={direction === "row" ? "flex flex-row" : "flex flex-col"} style={gap ? { gap: `${gap}px` } : undefined}>
      {items.map((item, i) => {
        const itemKey = (item != null && typeof item === "object" && "id" in item)
          ? String((item as Record<string, unknown>).id)
          : String(i)

        // Create a child context with item data available
        const childCtx = {
          ...ctx,
          itemContext: { ...ctx.itemContext, item },
          resolveSymbol: (expr: string) => {
            if (typeof expr === "string") {
              // Pure #{...} expression
              const pureMatch = /^#\{([^}]+)\}$/.exec(expr)
              if (pureMatch) {
                const inner = pureMatch[1].trim()
                if (inner === "item") return item
                // Simple item path (no operators) → direct lookup
                if (inner.startsWith("item.") && /^item\.[\w.[\]]+$/.test(inner)) {
                  const path = inner.slice(5)
                  return getNestedValue(item, path)
                }
                // Complex expression containing item references (ternary, comparison, etc.)
                // e.g., "item.highlighted ? theme.colors.primary : theme.colors.border"
                if (inner.includes("item.") || inner.includes("item ")) {
                  const replaced = inner.replace(/\bitem\.(\w+(?:\.\w+)*(?:\[\d+\])?)/g, (_m, path) => {
                    const val = getNestedValue(item, path)
                    if (val === null || val === undefined) return "null"
                    if (typeof val === "boolean") return String(val)
                    if (typeof val === "number") return String(val)
                    return `'${String(val)}'`
                  })
                  return ctx.resolveSymbol(`#{${replaced}}`)
                }
              }
              // Template with multiple #{...}
              if (!pureMatch && expr.includes("#{")) {
                let hasItemRef = false
                const resolved = expr.replace(/#\{([^}]+)\}/g, (_m, ref) => {
                  const r = (ref as string).trim()
                  if (r === "item") {
                    hasItemRef = true
                    return item != null ? String(item) : ""
                  }
                  // Simple item path
                  if (r.startsWith("item.") && /^item\.[\w.[\]]+$/.test(r)) {
                    hasItemRef = true
                    const path = r.slice(5)
                    const val = getNestedValue(item, path)
                    return val != null ? String(val) : ""
                  }
                  // Complex expression with item refs
                  if (r.includes("item.") || r.includes("item ")) {
                    hasItemRef = true
                    const replaced = r.replace(/\bitem\.(\w+(?:\.\w+)*(?:\[\d+\])?)/g, (_m2, path) => {
                      const val = getNestedValue(item, path)
                      if (val === null || val === undefined) return "null"
                      if (typeof val === "boolean") return String(val)
                      if (typeof val === "number") return String(val)
                      return `'${String(val)}'`
                    })
                    const resolved2 = ctx.resolveSymbol(`#{${replaced}}`)
                    return resolved2 != null ? String(resolved2) : ""
                  }
                  const val = ctx.resolveSymbol(`#{${ref}}`)
                  return val != null ? String(val) : ""
                })
                if (hasItemRef) return resolved
              }
            }
            return ctx.resolveSymbol(expr)
          },
        }

        // Clone the template with unique IDs per iteration
        const clonedTemplate = cloneElementWithIds(itemTemplate, `${element.id}-${i}`)

        return (
          <RenderContext.Provider key={itemKey} value={childCtx}>
            <ElementRenderer element={clonedTemplate} />
          </RenderContext.Provider>
        )
      })}
    </div>
  )
}

function cloneElementWithIds(el: CogoElement, suffix: string): CogoElement {
  return {
    ...el,
    id: `${el.id}-${suffix}`,
    children: el.children?.map((child) => cloneElementWithIds(child, suffix)),
  }
}

function getNestedValue(obj: unknown, path: string): unknown {
  // Split path by "." but also handle bracket notation: "features[0]" → ["features", "0"]
  const parts: string[] = []
  for (const segment of path.split(".")) {
    const bracketMatch = /^(\w+)\[(\d+)\]$/.exec(segment)
    if (bracketMatch) {
      parts.push(bracketMatch[1], bracketMatch[2])
    } else {
      parts.push(segment)
    }
  }
  let current: unknown = obj
  for (const part of parts) {
    if (current == null) return undefined
    if (Array.isArray(current)) {
      const idx = parseInt(part, 10)
      current = isNaN(idx) ? undefined : current[idx]
    } else if (typeof current === "object") {
      current = (current as Record<string, unknown>)[part]
    } else {
      return undefined
    }
  }
  return current
}
